// @ts-nocheck
import { useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronRight, Mail, RefreshCw } from 'lucide-react';
import { Badge } from '../../../../components/WorkflowFormPrimitives';
import { ErrorDetailModal } from '../../../../components/ErrorDetailModal';
import { TruncatedErrorCell } from '../../../../components/TruncatedErrorCell';

const STATUS_BADGE_VARIANT: Record<string, string> = {
  Pending: 'warning',
  Queued: 'default',
  Sent: 'warning',
  Failed: 'danger',
  Confirmed: 'success',
};

const STATUS_LABEL: Record<string, string> = {
  Pending: 'Pending',
  Queued: 'Queued',
  Sent: 'Delivered — Awaiting Click',
  Failed: 'Failed',
  Confirmed: 'Confirmed',
};

export const PreLaunchEmailCheckStep = ({
  eligibleShareholders,
  allEligiblePrecheckVerified,
  precheckSending,
  precheckPolling,
  precheckRefreshing,
  precheckPollTimedOut,
  precheckError,
  onBack,
  onSendPrecheck,
  onRefreshPrecheckStatus,
  onContinue,
}) => {
  const [errorDetail, setErrorDetail] = useState(null);
  const sendBusy = precheckSending || precheckPolling;
  const showManualActions = precheckPollTimedOut && !allEligiblePrecheckVerified && !precheckPolling;
  const awaitingShareholderClicks =
    eligibleShareholders.length > 0 &&
    !allEligiblePrecheckVerified &&
    eligibleShareholders.every((s) => {
      const status = s.emailPrecheckStatus || 'Pending';
      return status === 'Sent' || status === 'Confirmed';
    }) &&
    eligibleShareholders.some((s) => s.emailPrecheckStatus !== 'Confirmed');
  const sendButtonLabel = precheckSending
    ? 'Queueing...'
    : precheckPolling
      ? 'Checking Status...'
      : showManualActions
        ? 'Resend Pre-Launch Check'
        : 'Send Pre-Launch Check';

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5 space-y-4">
        <div>
          <p className="text-xs font-semibold text-gray-900">Pre-Launch Check</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Queue a pre-launch verification email for each eligible shareholder. Each recipient should click the
            confirmation link in the email; status moves to Confirmed after they do.
          </p>
        </div>

        <div className="rounded-md border border-gray-100 bg-gray-50/60 px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-gray-700">Eligible Shareholders</p>
            <p className="text-xs text-gray-500 mt-0.5">Must all be confirmed before launch step</p>
          </div>
          <span className="text-xs font-semibold text-gray-900 tabular-nums">{eligibleShareholders.length}</span>
        </div>

        <div className="border border-gray-100 rounded-md overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">Email Check Status</span>
            {allEligiblePrecheckVerified ? (
              <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 size={12} /> Ready
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                <AlertTriangle size={12} /> Action Required
              </span>
            )}
          </div>
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[30%]" />
              <col className="w-[40%]" />
              <col className="w-[15%]" />
              <col className="w-[15%]" />
            </colgroup>
            <thead className="bg-white border-b border-gray-100">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  Last Error
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {eligibleShareholders.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-xs text-gray-500 text-center">
                    No eligible shareholders to check.
                  </td>
                </tr>
              ) : (
                eligibleShareholders.map((sh) => {
                  const status = sh.emailPrecheckStatus || 'Pending';
                  const variant = STATUS_BADGE_VARIANT[status] ?? 'default';
                  return (
                    <tr key={sh.id} className="hover:bg-gray-50/40">
                      <td className="px-3 py-3 text-xs font-semibold text-gray-900 truncate">{sh.name}</td>
                      <td className="px-3 py-3 text-xs text-gray-600 truncate">{sh.email}</td>
                      <td className="px-3 py-3">
                        <Badge variant={variant} size="table">
                          {STATUS_LABEL[status] ?? status}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 max-w-[11rem]">
                        <TruncatedErrorCell
                          message={sh.emailPrecheckError}
                          subject={sh.name}
                          maxWidthClass="max-w-full"
                          onView={
                            sh.emailPrecheckError
                              ? () => setErrorDetail({ name: sh.name, message: sh.emailPrecheckError })
                              : undefined
                          }
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {precheckPolling ? (
          <div
            role="status"
            aria-live="polite"
            className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-2.5 text-xs text-blue-800 flex items-start space-x-2"
          >
            <RefreshCw size={13} className="mt-0.5 flex-shrink-0 text-blue-500 animate-spin" aria-hidden />
            <span>Waiting for delivery confirmation (up to 30 seconds)...</span>
          </div>
        ) : null}

        {awaitingShareholderClicks ? (
          <div
            role="status"
            aria-live="polite"
            className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5 text-xs text-amber-800 flex items-start space-x-2"
          >
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-amber-500" aria-hidden />
            <span>
              Verification emails were delivered. Waiting for each shareholder to click the confirmation link in their
              email.
            </span>
          </div>
        ) : null}

        {showManualActions ? (
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5 text-xs text-amber-800 flex items-start space-x-2">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-amber-500" />
            <span>
              Some emails are still queued or pending. Use Check Status to refresh, or resend the pre-launch check.
            </span>
          </div>
        ) : null}

        {precheckError ? (
          <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-2.5 text-xs text-red-700 flex items-start space-x-2">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0 text-red-400" />
            <span>{precheckError}</span>
          </div>
        ) : null}

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={onBack}
            disabled={sendBusy}
            className="inline-flex items-center px-3 py-2 rounded-md text-xs font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={13} className="mr-1 rotate-180" /> Back
          </button>
          <div className="flex items-center gap-2">
            {showManualActions ? (
              <button
                type="button"
                onClick={onRefreshPrecheckStatus}
                disabled={precheckRefreshing || sendBusy}
                aria-busy={precheckRefreshing}
                className="inline-flex items-center px-4 py-2 rounded-md text-xs font-semibold bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                {precheckRefreshing ? (
                  <RefreshCw size={13} className="mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw size={13} className="mr-1.5" />
                )}
                {precheckRefreshing ? 'Checking...' : 'Check Status'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onSendPrecheck}
              disabled={
                sendBusy || eligibleShareholders.length === 0 || (allEligiblePrecheckVerified && !precheckPollTimedOut)
              }
              aria-busy={sendBusy}
              className="inline-flex items-center px-4 py-2 rounded-md text-xs font-semibold bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              {sendBusy ? (
                <RefreshCw size={13} className="mr-1.5 animate-spin" />
              ) : (
                <Mail size={13} className="mr-1.5" />
              )}
              {sendButtonLabel}
            </button>
            <button
              type="button"
              onClick={onContinue}
              disabled={!allEligiblePrecheckVerified}
              className="inline-flex items-center px-4 py-2 rounded-md text-xs font-semibold bg-[#0f172a] text-white hover:bg-[#1e293b] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Continue to Launch Vote Room <ChevronRight size={13} className="ml-1.5" />
            </button>
          </div>
        </div>
      </div>

      <ErrorDetailModal
        open={!!errorDetail}
        title="Pre-Launch Email Error"
        subtitle={errorDetail?.name}
        message={errorDetail?.message}
        onClose={() => setErrorDetail(null)}
      />
    </div>
  );
};
